import apiService from '../ApiCallService';
import {
    BulkPromotionDetail,
    GetBulkPromotionsResponse,
} from '../../models/promotion/BulkPromotionTypes';
import { PromotionComponentType } from '../../models/promotion/PromotionTypes';
import { NamedComponent } from 'src/components/calculations/bulk-promote/bulkNameResolver';

/**
 * Reads for the bulk promotion surfaces. One operation underneath — GET /promotion/bulk serves both the
 * list and a single batch, keyed on whether bulkPromotionId is supplied — wrapped here as two typed methods
 * so callers state which read they want and get back only the field their call populates.
 *
 * Submission is not here: it goes over the existing performBulkAction WebSocket route.
 */
export default class BulkPromotionService {
    /** A page of the period's batches, newest first. The history list. */
    async listBulkPromotions(
        accountingPeriod: string,
        limit: number,
        nextToken?: string
    ): Promise<GetBulkPromotionsResponse> {
        const response = await apiService.getBulkPromotions({ accountingPeriod, limit, nextToken });
        /*
         * The members are read off the body rather than the envelope being returned as-is, so this method
         * honours what the class comment above promises and callers get only the field their call populates.
         * Returning `response.json()` handed back {promotions?, nextToken?, promotion?} and left every caller
         * handling `promotions === undefined` under strict mode.
         *
         * Reading `promotions` here is also what makes the member name a real regression guard. While this was
         * a pass-through, the test asserting it is `promotions` and not `bulkPromotions` was comparing against
         * properties of its own mock literal — the service never named the member, so a rename on either side
         * kept the suite green.
         */
        const body: GetBulkPromotionsResponse = await response.json();
        return { promotions: body.promotions ?? [], nextToken: body.nextToken };
    }

    /**
     * One batch's header, blocked rows and promoted ids. The resume path, and the expanded row.
     *
     * The period is required rather than optional: `accountingPeriod` carries both `@required` and
     * `@httpQuery` on GetBulkPromotionRequest, so a request without it is rejected before it reaches
     * the DAO. Sending the batch id alone made every read-back fail, which left the only recovery from
     * a lost push silently useless.
     */
    async getBulkPromotion(
        bulkPromotionId: string,
        accountingPeriod: string
    ): Promise<BulkPromotionDetail | undefined> {
        const response = await apiService.getBulkPromotions({ accountingPeriod, bulkPromotionId });
        // The batch is nested under `promotion` rather than being the body. Undefined is a real outcome —
        // an unknown or expired batch — so it is in the return type rather than cast away, which would
        // leave callers dereferencing it on the strength of a signature that was not true.
        const body: GetBulkPromotionsResponse = await response.json();
        return body.promotion;
    }

    /**
     * Every component of a type, as (id, name, status), so a pasted NAME can be resolved to the id a
     * promotion submits.
     *
     * Reuses the list operations the MDA and TPA screens already call — no new API. One call serves both TPA
     * types, because the response splits templates and worksheets into separate arrays, so the type selects
     * the array rather than being sniffed per item.
     *
     * Status is carried through unmapped: the resolver uses it only to prefer an active candidate when a name
     * is ambiguous, and these sources spell the vocabulary differently ("ACTIVE" vs "Active").
     *
     * Returns [] for a type with no list endpoint — CLI_CONFIG and PLI_CONFIG, whose ids are already the
     * readable identifier — so the caller needs no special case.
     */
    async listNamedComponents(componentType: string): Promise<NamedComponent[]> {
        if (componentType === PromotionComponentType.MDA_SHEET) {
            const body = await (await apiService.getMultiAllocationSheets()).json();
            return (body?.sheets || []).map((sheet: any) => ({
                componentId: sheet.sheetId,
                name: sheet.sheetName,
                status: sheet.status,
            }));
        }
        if (componentType === PromotionComponentType.MDA_WORKSHEET) {
            const body = await (await apiService.getMultiAllocationWorksheets()).json();
            return (body?.worksheets || []).map((worksheet: any) => ({
                componentId: worksheet.worksheetId,
                name: worksheet.worksheetName,
                status: worksheet.status,
            }));
        }
        if (componentType === PromotionComponentType.TPA_TEMPLATE
            || componentType === PromotionComponentType.TPA_WORKSHEET) {
            const body = await (await apiService.getTPAllocationTemplatesAndWorksheets()).json();
            if (componentType === PromotionComponentType.TPA_TEMPLATE) {
                return (body?.templates || []).map((template: any) => ({
                    componentId: template.templateId,
                    name: template.templateName,
                    status: template.status,
                }));
            }
            /*
             * Both places a worksheet can appear, not just the top-level array.
             *
             * The response nests a template's worksheets under `templates[].linkedWorksheets` and lists only
             * the standalone ones at the top level — see WorksheetList. Reading only the top level missed every
             * linked worksheet, which had two distinct symptoms in alpha: their names did not resolve at all,
             * and a name shared by a linked ACTIVE worksheet and a standalone INACTIVE one resolved to the
             * INACTIVE id, because with the active candidate absent there was only one candidate left and
             * nothing for the active-preference rule to compare.
             *
             * De-duplicated by id, since a worksheet reachable both ways must not become a false ambiguity —
             * two entries for one component would make the resolver refuse to choose and submit nothing.
             */
            const linked = (body?.templates || []).flatMap((template: any) => template?.linkedWorksheets || []);
            const byId = new Map<string, NamedComponent>();
            for (const worksheet of [...(body?.worksheets || []), ...linked]) {
                if (!worksheet?.worksheetId) {
                    continue;
                }
                byId.set(worksheet.worksheetId, {
                    componentId: worksheet.worksheetId,
                    name: worksheet.worksheetName,
                    status: worksheet.status,
                });
            }
            return Array.from(byId.values());
        }
        return [];
    }
}
